/**
 * Trovva — CSV Data Normalizer
 *
 * Transforms raw CSV data into Trovva-ready format after AI column mapping.
 * Handles:
 *   - Phone normalization: (609) 623-9236, 609-623-9236, 6096239236, +16096239236 → +16096239236
 *   - Address merging: street + city + state + zip → combined OR parse "123 Main, City, ST 07102"
 *   - Name splitting: "John Smith" → first_name: "John", last_name: "Smith"
 *   - Date parsing: 04/23/2026, 2026-04-23, April 23 2026, 4/23/26 → 2026-04-23
 *   - Money parsing: $450,000, 450000, $450K → 450000
 *   - State normalization: "New Jersey" → "NJ"
 *   - Boolean parsing: "yes", "true", "1", "y" → true
 */

// ── Phone Normalization ──────────────────────────────────────
export function normalizePhone(raw) {
  if (!raw) return '';
  // Strip everything except digits and leading +
  let digits = raw.replace(/[^\d+]/g, '');
  if (!digits) return '';

  // Remove leading + for digit counting
  const hasPlus = digits.startsWith('+');
  if (hasPlus) digits = digits.slice(1);

  // Handle various formats
  if (digits.length === 10) return '+1' + digits;           // 6096239236 → +16096239236
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;  // 16096239236 → +16096239236
  if (digits.length === 12 && digits.startsWith('91')) return '+' + digits; // Indian number
  if (hasPlus) return '+' + digits;                          // Already had country code

  // Fallback: if 7+ digits, assume US and pad
  if (digits.length >= 7) return '+1' + digits.slice(-10);

  return raw.trim(); // Can't normalize — return as-is
}

// ── Name Splitting ───────────────────────────────────────────
export function splitFullName(fullName) {
  if (!fullName) return { first_name: '', last_name: '' };
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  // Last part is last name, everything else is first name
  // Handles "Mary Jane Watson" → first: "Mary Jane", last: "Watson"
  return {
    first_name: parts.slice(0, -1).join(' '),
    last_name: parts[parts.length - 1],
  };
}

// ── Date Parsing ─────────────────────────────────────────────
const MONTH_MAP = {
  jan: '01', january: '01', feb: '02', february: '02', mar: '03', march: '03',
  apr: '04', april: '04', may: '05', jun: '06', june: '06',
  jul: '07', july: '07', aug: '08', august: '08', sep: '09', september: '09',
  oct: '10', october: '10', nov: '11', november: '11', dec: '12', december: '12',
};

export function normalizeDate(raw) {
  if (!raw) return '';
  const s = raw.trim();

  // ISO format: 2026-04-23 or 2026-04-23T...
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }

  // MM/DD/YYYY or MM-DD-YYYY
  const slashMatch = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (slashMatch) {
    let [, mm, dd, yyyy] = slashMatch;
    if (yyyy.length === 2) yyyy = (parseInt(yyyy) > 50 ? '19' : '20') + yyyy;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }

  // "April 23, 2026" or "Apr 23 2026"
  const namedMatch = s.match(/^(\w+)\s+(\d{1,2}),?\s*(\d{4})$/);
  if (namedMatch) {
    const [, month, day, year] = namedMatch;
    const mm = MONTH_MAP[month.toLowerCase()];
    if (mm) return `${year}-${mm}-${day.padStart(2, '0')}`;
  }

  // "23 April 2026" or "23-Apr-2026"
  const reversedMatch = s.match(/^(\d{1,2})[\s\-](\w+)[\s\-](\d{4})$/);
  if (reversedMatch) {
    const [, day, month, year] = reversedMatch;
    const mm = MONTH_MAP[month.toLowerCase()];
    if (mm) return `${year}-${mm}-${day.padStart(2, '0')}`;
  }

  // Fallback: try native Date parsing
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return d.toISOString().split('T')[0];
  }

  return ''; // Unparseable
}

// ── Money Parsing ────────────────────────────────────────────
export function normalizeMoney(raw) {
  if (!raw) return null;
  let s = raw.trim();

  // Handle K/M suffix: $450K → 450000, $1.5M → 1500000
  const kMatch = s.match(/^[\$]?\s*([\d,.]+)\s*[kK]$/);
  if (kMatch) return parseFloat(kMatch[1].replace(/,/g, '')) * 1000;

  const mMatch = s.match(/^[\$]?\s*([\d,.]+)\s*[mM]$/);
  if (mMatch) return parseFloat(mMatch[1].replace(/,/g, '')) * 1000000;

  // Strip $, commas, spaces
  s = s.replace(/[$,\s]/g, '');
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

// ── State Normalization ──────────────────────────────────────
const STATE_NAMES = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
  'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
  'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
  'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
  'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
  'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
  'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
  'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
  'wisconsin': 'WI', 'wyoming': 'WY', 'district of columbia': 'DC',
};

export function normalizeState(raw) {
  if (!raw) return '';
  const s = raw.trim();
  // Already a 2-letter code
  if (/^[A-Z]{2}$/.test(s.toUpperCase())) return s.toUpperCase();
  // Full name
  return STATE_NAMES[s.toLowerCase()] || s.toUpperCase().slice(0, 2);
}

// ── Boolean Parsing ──────────────────────────────────────────
export function normalizeBool(raw) {
  if (!raw) return false;
  return ['true', 'yes', 'y', '1', 'x', 'checked'].includes(raw.trim().toLowerCase());
}

// ── Full Address Parsing ─────────────────────────────────────
// "123 Main St, Newark, NJ 07102" → { address, city, state, zip }
export function parseFullAddress(raw) {
  if (!raw) return { address: '', city: '', state: '', zip: '' };
  const s = raw.trim();

  // Try "Street, City, ST ZIP" pattern
  const match = s.match(/^(.+?),\s*(.+?),\s*([A-Za-z]{2})\s*(\d{5}(?:-\d{4})?)?\s*$/);
  if (match) {
    return {
      address: match[1].trim(),
      city: match[2].trim(),
      state: normalizeState(match[3]),
      zip: (match[4] || '').trim(),
    };
  }

  // Try "Street, City, State ZIP" (full state name)
  const match2 = s.match(/^(.+?),\s*(.+?),\s*(\w[\w\s]*?)\s+(\d{5}(?:-\d{4})?)\s*$/);
  if (match2) {
    return {
      address: match2[1].trim(),
      city: match2[2].trim(),
      state: normalizeState(match2[3]),
      zip: match2[4].trim(),
    };
  }

  // Try "Street, City ST ZIP" (no comma before state)
  const match3 = s.match(/^(.+?),\s*(.+?)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/);
  if (match3) {
    return {
      address: match3[1].trim(),
      city: match3[2].trim(),
      state: normalizeState(match3[3]),
      zip: match3[4].trim(),
    };
  }

  // Can't parse — put everything in address
  return { address: s, city: '', state: '', zip: '' };
}

// ── Main Normalizer ──────────────────────────────────────────
// Takes raw CSV rows + AI mapping result, returns normalized rows ready for API.
//
// aiResult shape: {
//   mappings: { "0": { field: "first_name", confidence: 0.9 }, ... },
//   merges: [{ target: "address", columns: [3, 4], separator: " " }],
//   transforms: [{ column: 5, type: "split_name" }, { column: 8, type: "parse_full_address" }]
// }
export function normalizeRows(rawRows, aiResult, target) {
  const { mappings, merges = [], transforms = [] } = aiResult;

  // Phone fields that need normalization
  const PHONE_FIELDS = new Set(['phone', 'phone_secondary', 'client_phone']);
  const DATE_FIELDS = new Set(['offer_date', 'acceptance_date', 'closing_date',
    'attorney_review_start', 'attorney_review_end', 'inspection_date',
    'inspection_deadline', 'appraisal_date', 'mortgage_commitment_date',
    'clear_to_close_date', 'actual_closing_date', 'last_contacted']);
  const MONEY_FIELDS = new Set(['offer_price', 'sale_price', 'commission_amount',
    'agent_payout', 'brokerage_payout', 'budget', 'preapproval_amount']);
  const STATE_FIELDS = new Set(['state', 'property_state']);
  const BOOL_FIELDS = new Set(['preapproved']);
  const PCT_FIELDS = new Set(['commission_pct', 'agent_split_pct']);

  return rawRows.map(row => {
    const obj = {};

    // 1. Apply direct mappings
    Object.entries(mappings).forEach(([colIdx, info]) => {
      const field = typeof info === 'string' ? info : info?.field;
      if (!field) return;
      const val = row[parseInt(colIdx)] || '';
      obj[field] = val.trim();
    });

    // 2. Apply merges (e.g., Address 1 + Address 2)
    merges.forEach(merge => {
      const parts = merge.columns.map(ci => (row[ci] || '').trim()).filter(Boolean);
      if (parts.length) {
        obj[merge.target] = parts.join(merge.separator || ' ');
      }
    });

    // 3. Apply transforms
    transforms.forEach(t => {
      const val = row[t.column] || '';
      if (t.type === 'split_name' && val.trim()) {
        const { first_name, last_name } = splitFullName(val);
        const prefix = t.target_prefix || (target === 'deals' ? 'client_' : '');
        obj[prefix + 'first_name'] = first_name;
        obj[prefix + 'last_name'] = last_name;
      }
      if (t.type === 'parse_full_address' && val.trim()) {
        const parsed = parseFullAddress(val);
        const prefix = target === 'deals' ? 'property_' : '';
        obj[prefix + 'address'] = obj[prefix + 'address'] || parsed.address;
        // Only fill city/state/zip if not already mapped
        if (!obj[prefix + 'city']) obj[prefix + 'city'] = parsed.city;
        if (!obj[prefix + 'state']) obj[prefix + 'state'] = parsed.state;
        if (!obj[prefix + 'zip']) obj[prefix + 'zip'] = parsed.zip;
      }
    });

    // 4. Handle virtual fields
    if (obj.full_name) {
      const { first_name, last_name } = splitFullName(obj.full_name);
      if (!obj.first_name) obj.first_name = first_name;
      if (!obj.last_name) obj.last_name = last_name;
      delete obj.full_name;
    }
    if (obj.client_full_name) {
      const { first_name, last_name } = splitFullName(obj.client_full_name);
      if (!obj.client_first_name) obj.client_first_name = first_name;
      if (!obj.client_last_name) obj.client_last_name = last_name;
      delete obj.client_full_name;
    }
    if (obj.full_address) {
      const parsed = parseFullAddress(obj.full_address);
      const prefix = target === 'deals' ? 'property_' : '';
      if (!obj[prefix + 'address']) obj[prefix + 'address'] = parsed.address;
      if (!obj[prefix + 'city']) obj[prefix + 'city'] = parsed.city;
      if (!obj[prefix + 'state']) obj[prefix + 'state'] = parsed.state;
      if (!obj[prefix + 'zip']) obj[prefix + 'zip'] = parsed.zip;
      delete obj.full_address;
    }

    // 5. Normalize field values by type
    Object.keys(obj).forEach(key => {
      if (PHONE_FIELDS.has(key)) obj[key] = normalizePhone(obj[key]);
      else if (DATE_FIELDS.has(key)) obj[key] = normalizeDate(obj[key]);
      else if (MONEY_FIELDS.has(key)) obj[key] = normalizeMoney(obj[key]);
      else if (STATE_FIELDS.has(key)) obj[key] = normalizeState(obj[key]);
      else if (BOOL_FIELDS.has(key)) obj[key] = normalizeBool(obj[key]);
      else if (PCT_FIELDS.has(key)) {
        // Strip % sign and parse
        const raw = String(obj[key] || '').replace(/%/g, '').trim();
        const n = parseFloat(raw);
        obj[key] = isNaN(n) ? null : n;
      }
    });

    return obj;
  });
}
